import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { api, ApiError } from '../api';
import { can, fieldVisible } from '../auth/me';
import { useMe } from '../auth/useMe';
import { formatPln } from '../catalog/money';
import { PACKLISTS_RESOURCE, packlistKey, type LineView, type PacklistView } from '../packlists/types';
import { countsIn, groupRefs, groupsBySupplier, returnedLabel, selectedRefs, statusScopes, type StatusScope } from './groups';
import { RentalForm } from './RentalForm';
import { elsewhereNote, type ElsewhereItem } from './elsewhere';
import { useRentalReturn } from './rentalPick';
import { RentalMarkTag } from './RentalMarkTag';
import { StatusChooser } from './StatusChooser';
import { formatDays, presetOf, RENTAL_STATUSES, RENTALS_RESOURCE, rentalsKey, SUBRENTALS_KEY, type RentalPreset, type RentalStatus, type RentalView } from './types';
import { catalogGesture, catalogTitle, isMac, mayOpen, useCatalogOpener, type CatalogTarget } from '../nav/catalogLink';
import { CatalogRow } from '../nav/CatalogRow';
import './subrentals.css';

/** How long the server takes to follow a rental in the packlist (its outbox runs every half a second). */
const PACKLIST_FOLLOW_MS = 1500;

/**
 * Tab "Dopożyczenia" of the event card (F28, spec E2 §6.2): what we rent from suppliers for this event — gear,
 * quantity, supplier, days, cost, status — "+ Dopożycz", and the shortages of the packlist with "Dopożycz" at each.
 * Owner 2026-10-23: grouped by supplier („zamówienie u dostawcy”) with a sum per supplier and in total; the status of a
 * row is a button. Owner 2026-10-23 18:46: the checkbox column „Zaznacz” only selects rows (all of them in its header,
 * a supplier's in the group header); the status chooser then offers „Do zaznaczonych pozycji (N)”. The selection lives
 * in this tab and is cleared after a change of the selected rows.
 */
export function RentalsTab({ eventId }: { eventId: string }) {
  const { me } = useMe();
  const [form, setForm] = useState<{ rental?: RentalView; preset?: RentalPreset } | null>(null);
  // „Dopożycz” open again after „Wskaż w katalogu” (owner 2026-10-23 21:23): it lives in the address
  const rentalReturn = useRentalReturn();
  // owner 2026-10-23 20:53: Ctrl+klik (Windows: Alt+klik) on a rental opens the supplier's position with its editor
  const openInCatalog = useCatalogOpener();
  const targetOf = (r: RentalView): CatalogTarget | null =>
    r.supplierId && r.priceId ? { kind: 'supplier', supplierId: r.supplierId, priceId: r.priceId } : r.supplierId && r.itemId ? { kind: 'supplier', supplierId: r.supplierId, itemId: r.itemId } : null;
  const opener = (r: RentalView) => {
    const t = targetOf(r);
    return mayOpen(me, t) ? () => openInCatalog(t, r.itemName ?? 'Dopożyczenie') : undefined;
  };
  const list = useQuery({ queryKey: rentalsKey(eventId), queryFn: () => api<{ rentals: RentalView[] }>(`/api/subrentals/events/${eventId}/rentals`) });
  const queryClient = useQueryClient();
  const [quickError, setQuickError] = useState('');
  const [selected, setSelected] = useState<ReadonlySet<string>>(() => new Set());
  const quick = useMutation({
    mutationFn: async ({ rental, status, scope, ids }: { rental: RentalView; status: RentalStatus; scope: StatusScope; ids: readonly string[] }) => {
      const all = list.data?.rentals ?? [];
      if (scope === 'selected') {
        // one change for the selected rows, of any suppliers, all or nothing
        await api(`/api/subrentals/events/${eventId}/rentals/selected`, { method: 'PATCH', body: { rentals: selectedRefs(all, ids, rental.id), status } });
        return;
      }
      const group = scope === 'supplier' ? groupRefs(all, rental) : [];
      await api<RentalView>(`/api/subrentals/rentals/${rental.id}`, { method: 'PATCH', body: { version: rental.version, status, ...(group.length > 0 ? { group } : {}) } });
    },
    onMutate: () => setQuickError(''),
    onSuccess: (_, v) => {
      if (v.scope === 'selected') setSelected(new Set());
    },
    onError: (error) => setQuickError(error instanceof ApiError && error.status === 409 ? 'Ktoś w międzyczasie zmienił te dopożyczenia — wczytano aktualne, spróbuj jeszcze raz.' : error instanceof ApiError ? error.message : 'Nie udało się zmienić statusu'),
    onSettled: async () => {
      await Promise.all([queryClient.invalidateQueries({ queryKey: SUBRENTALS_KEY }), queryClient.invalidateQueries({ queryKey: packlistKey(eventId) })]);
      setTimeout(() => void queryClient.invalidateQueries({ queryKey: packlistKey(eventId) }), PACKLIST_FOLLOW_MS);
    },
  });
  const packlist = useQuery({
    queryKey: packlistKey(eventId),
    enabled: !!me && can(me, PACKLISTS_RESOURCE, 'view'),
    queryFn: () => api<PacklistView>(`/api/packlists/${eventId}`),
  });
  // owner 2026-10-24 16:15 (pkt 5): how much other events lack of the same items on these days (rule 7a: the server)
  const shortCount = (packlist.data?.lines ?? []).filter((l) => l.availability.missing > 0).length;
  const elsewhere = useQuery({
    queryKey: [...packlistKey(eventId), 'elsewhere', shortCount],
    enabled: !!me && can(me, PACKLISTS_RESOURCE, 'view') && can(me, RENTALS_RESOURCE, 'create') && shortCount > 0,
    queryFn: () => api<{ items: ElsewhereItem[] }>(`/api/packlists/${eventId}/shortages/elsewhere`),
  });
  if (!me) return null;
  const rentals = list.data?.rentals ?? [];
  const canAdd = can(me, RENTALS_RESOURCE, 'create');
  const canEdit = can(me, RENTALS_RESOURCE, 'edit');
  // the return of the event's gear to the warehouse (counts, units, damage, closing) — moved here from the packlist
  const canReturn = can(me, PACKLISTS_RESOURCE, 'edit');
  const seeCost = fieldVisible(me, RENTALS_RESOURCE, 'costGrosze');
  const short: LineView[] = (packlist.data?.lines ?? []).filter((l) => l.availability.missing > 0);
  // owner 2026-10-23: status changes on the row itself (rule 7a: only with the status field)
  const quickStatus = canEdit && fieldVisible(me, RENTALS_RESOURCE, 'status');
  const { groups, totalGrosze: total } = groupsBySupplier(rentals, seeCost);
  // review 2026-11-02: a rental cancelled with its event („Anulowane”) is never selected (its status is the program's)
  const selectable = rentals.filter(countsIn);
  // the selection: only rows still in the list count
  const chosen = selectable.filter((r) => selected.has(r.id)).map((r) => r.id);
  const toggle = (ids: readonly string[], on: boolean) =>
    setSelected((prev) => {
      const next = new Set(prev);
      for (const id of ids) {
        if (on) next.add(id);
        else next.delete(id);
      }
      return next;
    });
  /** A header checkbox over `ids`: checked when all are selected, „partly” when some. */
  const selectAll = (ids: readonly string[], label: string) => {
    const n = ids.filter((id) => selected.has(id)).length;
    return (
      <input
        type="checkbox"
        aria-label={label}
        checked={ids.length > 0 && n === ids.length}
        ref={(el) => {
          if (el) el.indeterminate = n > 0 && n < ids.length;
        }}
        onChange={(e) => toggle(ids, e.target.checked)}
      />
    );
  };
  /** Columns before „Koszt”. */
  const lead = 3 + (quickStatus ? 1 : 0);

  return (
    <div role="tabpanel" aria-label="Dopożyczenia" className="rentals-tab" data-feature-id="subrentals.tab">
      {list.isLoading && <p className="muted" role="status">Wczytywanie…</p>}
      {list.error && <p className="alert" role="alert">{list.error instanceof ApiError ? list.error.message : 'Nie udało się wczytać dopożyczeń'}</p>}
      {canAdd && short.length > 0 && (
        <ul className="rental-shortages" aria-label="Braki w sprzętówce">
          {short.map((l) => (
            <li key={l.id}>
              <span>
                {l.itemName} — brakuje {l.availability.missing}
                <ElsewhereNote item={elsewhere.data?.items.find((i) => i.itemId === l.itemId)} />
              </span>
              <button className="btn small" type="button" onClick={() => setForm({ preset: presetOf(l, packlist.data?.event) })}>
                Dopożycz
              </button>
            </li>
          ))}
        </ul>
      )}
      {list.data && rentals.length === 0 && <p className="muted">Nic nie dopożyczamy na to wydarzenie.</p>}
      {rentals.length > 0 && (
        <>
          {quickStatus && chosen.length > 0 && (
            <p className="rental-selection" role="status">
              <span>Zaznaczono {chosen.length}</span>
              <span aria-hidden="true">—</span>
              <button className="link-button" type="button" onClick={() => setSelected(new Set())}>
                Odznacz
              </button>
            </p>
          )}
          <table className="table rentals-table">
            <thead>
              <tr>
                {quickStatus && (
                  <th scope="col" className="rental-select-col">
                    <label title="Zaznacz pozycje, potem w statusie wybierz „Do zaznaczonych pozycji”">
                      {selectAll(
                        selectable.map((r) => r.id),
                        'Zaznacz wszystkie pozycje',
                      )}
                      <span>Zaznacz</span>
                    </label>
                  </th>
                )}
                <th scope="col">Sprzęt</th>
                <th scope="col" className="num">
                  Ilość
                </th>
                <th scope="col">Dni</th>
                {seeCost && (
                  <th scope="col" className="num">
                    Koszt
                  </th>
                )}
                <th scope="col">Status</th>
              </tr>
            </thead>
            {groups.map((g) => (
              <tbody key={g.supplierId ?? ''} className="rental-group" aria-label={g.supplierName ? `Dostawca: ${g.supplierName}` : 'Dopożyczenia'}>
                {g.supplierName !== null && (
                  <tr className="rental-group-head">
                    {quickStatus && <td className="rental-select-col">{selectAll(g.rentals.filter(countsIn).map((r) => r.id), `Zaznacz wszystkie pozycje od ${g.supplierName}`)}</td>}
                    <th scope="colgroup" colSpan={lead - (quickStatus ? 1 : 0) + (seeCost ? 2 : 1)}>
                      <span>{g.supplierName}</span>
                      {fieldVisible(me, RENTALS_RESOURCE, 'status') && <span className="muted small rental-group-status">{returnedLabel(g.returned, g.counted)}</span>}
                    </th>
                  </tr>
                )}
                {g.rentals.map((r) => (
                  <CatalogRow
                    key={r.id}
                    open={opener(r)}
                    // owner 2026-11-01: cancelled with the event — struck through and greyed (it counts in no sum)
                    className={[r.status === 'zwrocone' ? 'done' : '', !countsIn(r) ? 'rental-cancelled' : '', selected.has(r.id) ? 'selected' : ''].join(' ').trim() || undefined}
                  >
                    {quickStatus && !countsIn(r) && <td className="rental-select-col" />}
                    {quickStatus && countsIn(r) && (
                      <td className="rental-select-col">
                        <input type="checkbox" checked={selected.has(r.id)} aria-label={`Zaznacz: ${r.itemName ?? 'dopożyczenie'}`} onChange={(e) => toggle([r.id], e.target.checked)} />
                      </td>
                    )}
                    <td title={opener(r) ? catalogTitle() : undefined}>
                      {canEdit ? (
                        // Alt+klik (Windows) on the name opens the catalog, not the form
                        <button className="link-button" type="button" data-catalog-link="" onClick={(e) => {
                          if (!(opener(r) && catalogGesture(e, isMac()))) setForm({ rental: r });
                        }}>
                          {r.itemName ?? 'Dopożyczenie'}
                        </button>
                      ) : (
                        (r.itemName ?? 'Dopożyczenie')
                      )}
                      {r.keptByQuote && <span className="muted small rental-note">{r.costOnly ? 'sam koszt (z kosztorysu)' : 'z kosztorysu'}</span>}
                      {r.itemId === null && !r.costOnly && !r.offList && <span className="muted small rental-note">spoza naszego katalogu</span>}
                      {/* owner 2026-10-24 16:15 (pkt 4): a supplier off our list, and what is still to fill in */}
                      {r.offList && (
                        <span className="small rental-note rental-off">
                          spoza listy dostawców{r.offSupplierName ? ` · ${r.offSupplierName}` : ''}
                          {r.contactPhone ? ` · tel. ${r.contactPhone}` : ''}
                        </span>
                      )}
                      {r.offList && r.description && <span className="muted small rental-note">{r.description}</span>}
                      {r.toFill && r.toFill.length > 0 &&
                        (canEdit ? (
                          <button className="link-button small rental-note rental-tofill" type="button" onClick={() => setForm({ rental: r })}>
                            do uzupełnienia: {r.toFill.join(', ')}
                          </button>
                        ) : (
                          <span className="small rental-note rental-tofill">do uzupełnienia: {r.toFill.join(', ')}</span>
                        ))}
                      <RentalMarkTag mark={r.mark} />
                      {r.outsideQuote && <span className="small rental-note rental-outside">poza obowiązującą wersją kosztorysu</span>}
                      {r.notes && <span className="muted small rental-note">{r.notes}</span>}
                    </td>
                    <td className="num">{r.quantity ?? ''}</td>
                    <td>
                      {formatDays(r.firstDay, r.lastDay)}
                      {r.days !== undefined && <span className="muted small"> ({r.days} {r.days === 1 ? 'dzień' : 'dni'})</span>}
                    </td>
                    {seeCost && <td className="num">{r.costGrosze !== undefined ? formatPln(r.costGrosze) : ''}</td>}
                    <td>
                      {r.status && quickStatus && r.status !== 'anulowane' ? (
                        <StatusChooser
                          status={r.status}
                          scopes={statusScopes({ rentalId: r.id, selected: chosen, supplierName: g.supplierName, supplierOthers: g.supplierId ? g.rentals.length - 1 : 0 })}
                          busy={quick.isPending}
                          onPick={(status, scope) => quick.mutate({ rental: r, status, scope, ids: chosen })}
                        />
                      ) : r.status ? (
                        RENTAL_STATUSES[r.status]
                      ) : (
                        ''
                      )}
                      {r.returnPersonName && <span className="muted small rental-note">oddaje: {r.returnPersonName}</span>}
                    </td>
                  </CatalogRow>
                ))}
                {g.totalGrosze !== null && g.supplierName !== null && groups.length > 1 && (
                  <tr className="rental-group-sum">
                    <th scope="row" colSpan={lead}>
                      Razem {g.supplierName}
                    </th>
                    <td className="num">{formatPln(g.totalGrosze)}</td>
                    <td />
                  </tr>
                )}
              </tbody>
            ))}
            {total !== null && (
              <tfoot>
                <tr>
                  <th scope="row" colSpan={lead}>
                    Razem dopożyczenia
                  </th>
                  <td className="num">{formatPln(total)}</td>
                  <td />
                </tr>
              </tfoot>
            )}
          </table>
          {quickError && (
            <p className="alert" role="alert">
              {quickError}
            </p>
          )}
        </>
      )}
      {(canAdd || canReturn) && (
        <div className="actions">
          {canAdd && (
            <button className="btn small primary" type="button" onClick={() => setForm({})}>
              + Dopożycz
            </button>
          )}
          {/* owner 2026-10-21 23:37: "zwroty nie powinny być w sprzętówce, tylko w dopożyczeniach" */}
          {canReturn && (
            <Link className="btn small" to={`/wydarzenia/${eventId}/zwrot`} data-feature-id="packlists.return">
              Zwrot sprzętu
            </Link>
          )}
        </div>
      )}
      {form && <RentalForm eventId={eventId} {...(form.rental ? { rental: form.rental, group: groupRefs(rentals, form.rental) } : {})} {...(form.preset ? { preset: form.preset } : {})} onClose={() => setForm(null)} />}
      {!form && rentalReturn && <RentalForm eventId={eventId} preset={rentalReturn.preset} picked={rentalReturn.picked} onClose={rentalReturn.close} />}
    </div>
  );
}

/** „ (na inne wydarzenia łącznie 5: Wesele A, Koncert B)” — each name a link to that event's packlist (when seen). */
function ElsewhereNote({ item }: { item: ElsewhereItem | undefined }) {
  const note = elsewhereNote(item);
  if (!note) return null;
  return (
    <span className="rental-elsewhere">
      {' ('}
      {note.lead}
      {note.events.map((e, i) => (
        <span key={e.key}>
          {i > 0 && ', '}
          {e.href ? (
            <Link to={e.href} title={e.title}>
              {e.name}
            </Link>
          ) : (
            <span title={e.title}>{e.name}</span>
          )}
        </span>
      ))}
      {')'}
    </span>
  );
}
